import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { listDepartments, syncDepartmentsFromApi } from "@/lib/hr/departmentsStore";
import { syncEmployeesFromApi } from "@/lib/hr/employeesStore";
import {
  buildCalendarWeeksMondayFirst,
  buildCopyFromPreviousMonthEntries,
  buildSaveEntriesForEmployee,
  buildSaveEntriesForEmployees,
  fetchRosterMonth,
  previousCalendarMonth,
  rosterApiErrorMessage,
  rosterCellKey,
  rosterCellLetter,
  rosterCellTimeLabel,
  ROSTER_WEEKDAY_HEADERS,
  saveRosterEntries,
  shiftLetterColorClass,
  shiftLegendLetter,
  type RosterCell,
  type RosterDayMeta,
  type RosterEmployeeRow,
  type RosterMonthData,
} from "@/lib/hr/rostersStore";
import { listWorkShifts, syncWorkShiftsFromApi, type WorkShiftRecord } from "@/lib/hr/workShiftsStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { addMonths, format } from "date-fns";
import { motion } from "framer-motion";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Copy,
  Loader2,
  RefreshCw,
  Save,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

type PendingChange = {
  employeeId: string;
  rosterDate: string;
  workShiftId: string | null;
};

type ShiftPickerState = {
  employeeId: string;
  employeeName: string;
  day: RosterDayMeta;
  currentShiftId: string | null;
} | null;

function effectiveCell(row: RosterEmployeeRow, date: string, pending: Map<string, PendingChange>): RosterCell {
  const key = rosterCellKey(row.id, date);
  const pendingChange = pending.get(key);
  if (pendingChange) {
    return pendingChange.workShiftId
      ? {
          workShiftId: pendingChange.workShiftId,
          workShiftName: null,
          workShiftSlug: null,
          startTime: null,
          endTime: null,
          source: "roster",
        }
      : {
          workShiftId: null,
          workShiftName: null,
          workShiftSlug: null,
          startTime: null,
          endTime: null,
          source: "roster",
        };
  }
  return (
    row.cells[date] ?? {
      workShiftId: null,
      workShiftName: null,
      workShiftSlug: null,
      startTime: null,
      endTime: null,
      source: "none",
    }
  );
}

function enrichCell(cell: RosterCell, shiftOptions: WorkShiftRecord[]): RosterCell {
  if (!cell.workShiftId) return cell;
  const hit = shiftOptions.find((s) => s.id === cell.workShiftId);
  if (!hit) return cell;
  return {
    ...cell,
    workShiftName: cell.workShiftName || hit.name,
    workShiftSlug: cell.workShiftSlug || hit.slug,
    startTime: cell.startTime || hit.startTime,
    endTime: cell.endTime || hit.endTime,
  };
}

function employeeShiftStats(row: RosterEmployeeRow, days: RosterDayMeta[], pending: Map<string, PendingChange>) {
  let workDays = 0;
  let assigned = 0;
  for (const day of days) {
    if (day.dayType === "holiday") continue;
    const cell = effectiveCell(row, day.date, pending);
    if (cell.source === "employee_off") continue;
    workDays++;
    if (cell.workShiftId) assigned++;
  }
  return { workDays, assigned };
}

type RosterMonthGridProps = {
  days: RosterDayMeta[];
  row: RosterEmployeeRow;
  shiftOptions: WorkShiftRecord[];
  pending: Map<string, PendingChange>;
  canUpdate: boolean;
  compact?: boolean;
  onPickDay: (state: ShiftPickerState) => void;
};

function RosterMonthGrid({
  days,
  row,
  shiftOptions,
  pending,
  canUpdate,
  compact = false,
  onPickDay,
}: RosterMonthGridProps) {
  const weeks = useMemo(() => buildCalendarWeeksMondayFirst(days), [days]);
  const todayIso = format(new Date(), "yyyy-MM-dd");
  const cellGap = compact ? "gap-1 sm:gap-1.5" : "gap-2";
  const cellHeight = compact ? "h-[3.35rem] sm:h-[3.65rem]" : "h-[3.75rem] sm:h-[4rem]";

  return (
    <div className={cn("w-full font-sans", !compact && "max-w-lg")}>
      <div className={cn("grid grid-cols-7 mb-1.5", cellGap)}>
        {ROSTER_WEEKDAY_HEADERS.map((label) => (
          <div
            key={label}
            className={cn(
              "text-center font-medium text-muted-foreground uppercase tracking-wide",
              compact ? "text-[10px]" : "text-[11px] py-1"
            )}
          >
            {compact ? label.slice(0, 2) : label}
          </div>
        ))}
      </div>

      <div className={cn("flex flex-col", compact ? "gap-1 sm:gap-1.5" : "gap-2")}>
        {weeks.map((week) => (
          <div key={week.index} className={cn("grid grid-cols-7", cellGap)}>
            {week.days.map((day, idx) => {
              if (!day) {
                return <div key={`empty-${week.index}-${idx}`} className={cellHeight} aria-hidden />;
              }

              const cell = enrichCell(effectiveCell(row, day.date, pending), shiftOptions);
              const key = rosterCellKey(row.id, day.date);
              const isPending = pending.has(key);
              const isEmployeeOff = cell.source === "employee_off";
              const isHoliday = day.dayType === "holiday";
              const isNonWork = isHoliday || isEmployeeOff;
              const isToday = day.date === todayIso;
              const letter = rosterCellLetter(cell, day, shiftOptions);
              const shiftId = isNonWork ? null : cell.workShiftId;
              const dashed = !isPending && !isNonWork && (cell.source === "assignment" || cell.source === "none");
              const timeLabel = rosterCellTimeLabel(cell);
              const title =
                isHoliday
                  ? day.holidayTitle || "Holiday"
                  : isEmployeeOff
                    ? "Off"
                    : [cell.workShiftName || (cell.workShiftId ? "Shift" : "Off"), timeLabel].filter(Boolean).join(" · ");

              return (
                <button
                  key={day.date}
                  type="button"
                  disabled={!canUpdate || isNonWork}
                  title={`${day.dayOfMonth} · ${title}`}
                  onClick={() =>
                    canUpdate && !isNonWork
                      ? onPickDay({
                          employeeId: row.id,
                          employeeName: row.fullName,
                          day,
                          currentShiftId: cell.workShiftId,
                        })
                      : undefined
                  }
                  className={cn(
                    "w-full flex flex-col items-center justify-center rounded-lg border border-border/50 bg-card text-foreground shadow-sm transition-all font-sans",
                    cellHeight,
                    compact ? "gap-0.5 px-0.5" : "gap-1 px-1",
                    isNonWork && "bg-muted/25 border-border/40 cursor-default shadow-none",
                    !isNonWork && dashed && "border-dashed opacity-90",
                    isPending && "ring-2 ring-primary/40 border-primary/50",
                    isToday && "ring-1 ring-primary/30 border-primary/40",
                    canUpdate && !isNonWork && "hover:bg-muted/15 hover:shadow-md cursor-pointer"
                  )}
                >
                  <span
                    className={cn(
                      "tabular-nums leading-none font-medium text-muted-foreground",
                      compact ? "text-xs sm:text-sm" : "text-sm",
                      isToday && "font-semibold text-foreground"
                    )}
                  >
                    {day.dayOfMonth}
                  </span>
                  <span
                    className={cn(
                      "font-semibold leading-none tracking-tight",
                      compact ? "text-sm sm:text-base" : "text-base",
                      isNonWork || letter === "O"
                        ? "text-muted-foreground"
                        : shiftLetterColorClass(shiftId)
                    )}
                  >
                    {letter}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

type EmployeeRosterCardProps = {
  row: RosterEmployeeRow;
  days: RosterDayMeta[];
  shiftOptions: WorkShiftRecord[];
  pending: Map<string, PendingChange>;
  canUpdate: boolean;
  index: number;
  selected: boolean;
  previousMonthLabel: string;
  copyingFromPrevious: boolean;
  savingEmployee: boolean;
  saveableCount: number;
  onToggleSelect: (employeeId: string, checked: boolean) => void;
  onPickDay: (state: ShiftPickerState) => void;
  onSaveEmployee: (row: RosterEmployeeRow) => void;
  onCopyFromPreviousMonth: (row: RosterEmployeeRow) => void;
};

function EmployeeRosterCard({
  row,
  days,
  shiftOptions,
  pending,
  canUpdate,
  index,
  selected,
  previousMonthLabel,
  copyingFromPrevious,
  savingEmployee,
  saveableCount,
  onToggleSelect,
  onPickDay,
  onSaveEmployee,
  onCopyFromPreviousMonth,
}: EmployeeRosterCardProps) {
  const stats = employeeShiftStats(row, days, pending);
  const pendingForEmployee = Array.from(pending.keys()).filter((k) => k.startsWith(`${row.id}|`)).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.24) }}
      className={cn(
        "glass-card group flex h-full flex-col overflow-hidden rounded-xl border bg-card/70 shadow-sm transition-shadow hover:shadow-md",
        selected ? "border-primary/50 ring-2 ring-primary/20" : "border-border/40"
      )}
    >
      <div className="border-b border-border/30 px-3 pt-3 pb-2.5">
        <div className="flex items-start justify-between gap-2 min-w-0">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            {canUpdate ? (
              <Checkbox
                className="mt-0.5"
                checked={selected}
                aria-label={`Select ${row.fullName}`}
                onCheckedChange={(v) => onToggleSelect(row.id, v === true)}
              />
            ) : null}
            <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold text-sm text-foreground truncate leading-snug" title={row.fullName}>
              {row.fullName || "—"}
            </h3>
            <p className="text-[11px] text-muted-foreground truncate mt-0.5">
              {[row.employeeCode, row.departmentName].filter(Boolean).join(" · ") || "—"}
            </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {canUpdate ? (
              <>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      disabled={savingEmployee || saveableCount === 0}
                      aria-label={`Save roster for ${row.fullName}`}
                      onClick={() => onSaveEmployee(row)}
                    >
                      {savingEmployee ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Save className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" sideOffset={6}>
                    {saveableCount > 0 ? `Save ${saveableCount} day(s)` : "No changes to save"}
                  </TooltipContent>
                </Tooltip>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      disabled={copyingFromPrevious || savingEmployee}
                      aria-label={`Copy roster from ${previousMonthLabel}`}
                      onClick={() => onCopyFromPreviousMonth(row)}
                    >
                      {copyingFromPrevious ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" sideOffset={6}>
                    Copy from {previousMonthLabel}
                  </TooltipContent>
                </Tooltip>
              </>
            ) : null}
            {pendingForEmployee > 0 ? (
              <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold">
                {pendingForEmployee} edit{pendingForEmployee === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">
          <span className="font-semibold text-foreground">{stats.assigned}</span>
          <span className="text-muted-foreground">/{stats.workDays} shifts</span>
        </p>
      </div>

      <div className="px-3 pb-3 pt-1 flex-1">
        <RosterMonthGrid
          days={days}
          row={row}
          shiftOptions={shiftOptions}
          pending={pending}
          canUpdate={canUpdate}
          compact
          onPickDay={onPickDay}
        />
      </div>
    </motion.div>
  );
}

export default function Rosters() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canUpdate = can(user, "roster_can_update");
  const [isForbidden, setIsForbidden] = useState(false);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [departmentId, setDepartmentId] = useState("");

  const [data, setData] = useState<RosterMonthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingEmployeeId, setSavingEmployeeId] = useState<string | null>(null);
  const [pending, setPending] = useState<Map<string, PendingChange>>(() => new Map());
  const [shiftPicker, setShiftPicker] = useState<ShiftPickerState>(null);
  const [copyingEmployeeId, setCopyingEmployeeId] = useState<string | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<string>>(() => new Set());

  const [deptTick, setDeptTick] = useState(0);
  const [shiftTick, setShiftTick] = useState(0);
  const departments = useMemo(() => listDepartments(), [deptTick]);
  const allShifts = useMemo(() => listWorkShifts().filter((s) => s.active), [shiftTick]);

  const shiftOptions = useMemo(() => {
    const byId = new Map(allShifts.map((s) => [s.id, s]));
    for (const s of data?.workShifts ?? []) {
      if (!byId.has(s.id)) byId.set(s.id, { ...s, createdAt: "", updatedAt: "" });
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allShifts, data?.workShifts]);

  const previousMonth = useMemo(() => {
    const cal = previousCalendarMonth(year, month);
    const labelDate = new Date(cal.year, cal.month - 1, 1);
    return { ...cal, label: format(labelDate, "MMMM yyyy") };
  }, [year, month]);

  const load = useCallback(async (options?: { preservePendingEmployeeIds?: Set<string> }) => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const monthData = await fetchRosterMonth({
        year,
        month,
        departmentId: departmentId || undefined,
      });
      setData(monthData);
      setSelectedEmployeeIds(new Set());
      if (options?.preservePendingEmployeeIds) {
        setPending((prev) => {
          const next = new Map<string, PendingChange>();
          for (const [key, change] of prev) {
            if (options.preservePendingEmployeeIds!.has(change.employeeId)) {
              next.set(key, change);
            }
          }
          return next;
        });
      } else {
        setPending(new Map());
      }
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setData(null);
        return;
      }
      toast.error(rosterApiErrorMessage(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, departmentId]);

  useEffect(() => {
    void Promise.all([
      syncDepartmentsFromApi().catch(() => {}),
      syncEmployeesFromApi().catch(() => {}),
      syncWorkShiftsFromApi().catch(() => {}),
    ]).finally(() => {
      setDeptTick((t) => t + 1);
      setShiftTick((t) => t + 1);
    });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const goMonth = (delta: number) => {
    if (pending.size > 0) {
      toast.message("Save your changes before changing month.");
      return;
    }
    const d = addMonths(new Date(year, month - 1, 1), delta);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const goToday = () => {
    if (pending.size > 0) {
      toast.message("Save your changes before jumping to today.");
      return;
    }
    const d = new Date();
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const visibleEmployeeIds = useMemo(() => data?.employees.map((e) => e.id) ?? [], [data?.employees]);

  const selectedCount = useMemo(() => {
    let n = 0;
    for (const id of visibleEmployeeIds) {
      if (selectedEmployeeIds.has(id)) n++;
    }
    return n;
  }, [visibleEmployeeIds, selectedEmployeeIds]);

  const allSelected = visibleEmployeeIds.length > 0 && selectedCount === visibleEmployeeIds.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const saveableByEmployeeId = useMemo(() => {
    const map = new Map<string, number>();
    if (!data) return map;
    for (const row of data.employees) {
      map.set(row.id, buildSaveEntriesForEmployee(row, data.days, pending).length);
    }
    return map;
  }, [data, pending]);

  const selectedSaveableCount = useMemo(() => {
    if (!data || selectedCount === 0) return 0;
    return buildSaveEntriesForEmployees(data.employees, data.days, pending, selectedEmployeeIds).length;
  }, [data, pending, selectedCount, selectedEmployeeIds]);

  const topSaveableCount = selectedSaveableCount;

  const toggleEmployeeSelect = (employeeId: string, checked: boolean) => {
    setSelectedEmployeeIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(employeeId);
      else next.delete(employeeId);
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedEmployeeIds(checked ? new Set(visibleEmployeeIds) : new Set());
  };

  const setCellShift = (employeeId: string, rosterDate: string, workShiftId: string | null) => {
    const key = rosterCellKey(employeeId, rosterDate);
    setPending((prev) => {
      const next = new Map(prev);
      const original = data?.employees.find((e) => e.id === employeeId)?.cells[rosterDate];
      const originalId = original?.workShiftId ?? null;
      if (originalId === workShiftId) {
        next.delete(key);
      } else {
        next.set(key, { employeeId, rosterDate, workShiftId });
      }
      return next;
    });
    setShiftPicker(null);
  };

  const handleSaveAll = async () => {
    if (!data) return;
    if (selectedCount === 0) {
      toast.message("Select at least one employee to save.");
      return;
    }
    const entries = buildSaveEntriesForEmployees(data.employees, data.days, pending, selectedEmployeeIds);
    if (entries.length === 0) {
      toast.message("No changes to save for selected employees.");
      return;
    }
    setSaving(true);
    try {
      const result = await saveRosterEntries(entries);
      toast.success(result.message);
      const savedEmployeeIds = new Set(entries.map((e) => e.employeeId));
      const preserve = new Set(
        Array.from(pending.values())
          .filter((e) => !savedEmployeeIds.has(e.employeeId))
          .map((e) => e.employeeId)
      );
      await load({ preservePendingEmployeeIds: preserve });
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmployee = async (row: RosterEmployeeRow) => {
    if (!data) return;
    const entries = buildSaveEntriesForEmployee(row, data.days, pending);
    if (entries.length === 0) {
      toast.message(`No changes to save for ${row.fullName || "this employee"}.`);
      return;
    }
    setSavingEmployeeId(row.id);
    try {
      const result = await saveRosterEntries(entries);
      toast.success(result.message);
      const preserve = new Set(
        Array.from(pending.values())
          .filter((e) => e.employeeId !== row.id)
          .map((e) => e.employeeId)
      );
      await load({ preservePendingEmployeeIds: preserve });
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
    } finally {
      setSavingEmployeeId(null);
    }
  };

  const handleCopyFromPreviousMonth = async (row: RosterEmployeeRow) => {
    if (!canUpdate || !data) return;

    setCopyingEmployeeId(row.id);
    try {
      const prevData = await fetchRosterMonth({
        year: previousMonth.year,
        month: previousMonth.month,
        employeeId: row.id,
      });
      const prevRow = prevData.employees.find((e) => e.id === row.id);
      if (!prevRow) {
        toast.message(`No roster found for ${row.fullName} in ${previousMonth.label}.`);
        return;
      }

      const hasSavedInPrevious = prevData.days.some(
        (day) => prevRow.cells[day.date]?.source === "roster"
      );
      if (!hasSavedInPrevious) {
        toast.message(`No saved roster in ${previousMonth.label} to copy.`);
        return;
      }

      const entries = buildCopyFromPreviousMonthEntries({
        employeeId: row.id,
        currentDays: data.days,
        currentCells: row.cells,
        previousDays: prevData.days,
        previousCells: prevRow.cells,
      });

      if (entries.length === 0) {
        toast.message(`This month already matches ${previousMonth.label}.`);
        return;
      }

      setPending((prev) => {
        const next = new Map(prev);
        for (const entry of entries) {
          const key = rosterCellKey(entry.employeeId, entry.rosterDate);
          const originalId = row.cells[entry.rosterDate]?.workShiftId ?? null;
          if (originalId === entry.workShiftId) {
            next.delete(key);
          } else {
            next.set(key, entry);
          }
        }
        return next;
      });

      toast.success(`Copied ${entries.length} day(s) from ${previousMonth.label}. Save to apply.`);
    } catch (e) {
      toast.error(rosterApiErrorMessage(e));
    } finally {
      setCopyingEmployeeId(null);
    }
  };

  const monthLabel = data?.monthLabel || format(new Date(year, month - 1, 1), "MMMM yyyy");

  return isForbidden ? (
    <PermissionDenied
      title="Rosters access required"
      message="You don’t have permission to view employee rosters. Ask an administrator to grant roster_can_view_any."
    />
  ) : (
    <Layout title="Rosters">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Employee rosters</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Monthly shift grid per employee — tap a day on any card to assign a shift.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {canUpdate ? (
              <Button
                type="button"
                size="sm"
                onClick={() => void handleSaveAll()}
                disabled={loading || saving || selectedCount === 0 || topSaveableCount === 0}
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save{topSaveableCount > 0 ? ` (${topSaveableCount})` : ""}
              </Button>
            ) : null}
            <Button type="button" variant="outline" size="icon" onClick={() => void load()} disabled={loading || saving}>
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
          <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-card px-2 py-1.5 w-fit shrink-0">
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => goMonth(-1)} disabled={loading}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-[9rem] text-center">
              <p className="text-sm font-semibold text-foreground">{monthLabel}</p>
              <button
                type="button"
                className="text-[11px] text-muted-foreground hover:text-primary"
                onClick={goToday}
              >
                Today
              </button>
            </div>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => goMonth(1)} disabled={loading}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-1.5 flex-1 max-w-xs">
            <Label htmlFor="roster-department">Department</Label>
            <Select
              value={departmentId || "__all__"}
              onValueChange={(v) => {
                if (pending.size > 0) {
                  toast.message("Save your changes before changing department.");
                  return;
                }
                setDepartmentId(v === "__all__" ? "" : v);
              }}
            >
              <SelectTrigger id="roster-department">
                <SelectValue placeholder="All departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All departments</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {shiftOptions.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-sans">
            <span className="font-medium text-muted-foreground">Legend</span>
            {shiftOptions.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5">
                <span className={cn("font-semibold tabular-nums", shiftLetterColorClass(s.id))}>
                  {shiftLegendLetter(s.name)}
                </span>
                <span className="text-muted-foreground">{s.name}</span>
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5">
              <span className="font-semibold text-muted-foreground">O</span>
              <span className="text-muted-foreground">Off</span>
            </span>
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground rounded-xl border border-border/60 bg-card">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading rosters for {monthLabel}…
          </div>
        ) : !data || data.employees.length === 0 ? (
          <div className="py-24 text-center text-sm text-muted-foreground rounded-xl border border-border/60 bg-card">
            <CalendarDays className="h-10 w-10 mx-auto mb-3 opacity-50" />
            No active employees found for this department.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {data.employees.length} employee{data.employees.length === 1 ? "" : "s"} · {monthLabel}
                {canUpdate && selectedCount > 0 ? (
                  <span className="text-foreground font-medium"> · {selectedCount} selected</span>
                ) : null}
              </p>
              {canUpdate ? (
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={(v) => toggleSelectAll(v === true)}
                    aria-label="Select all employees"
                  />
                  <span className="text-muted-foreground">Select all</span>
                </label>
              ) : null}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              {data.employees.map((row, i) => (
                <EmployeeRosterCard
                  key={row.id}
                  row={row}
                  days={data.days}
                  shiftOptions={shiftOptions}
                  pending={pending}
                  canUpdate={canUpdate}
                  index={i}
                  selected={selectedEmployeeIds.has(row.id)}
                  previousMonthLabel={previousMonth.label}
                  copyingFromPrevious={copyingEmployeeId === row.id}
                  savingEmployee={savingEmployeeId === row.id}
                  saveableCount={saveableByEmployeeId.get(row.id) ?? 0}
                  onToggleSelect={toggleEmployeeSelect}
                  onPickDay={setShiftPicker}
                  onSaveEmployee={handleSaveEmployee}
                  onCopyFromPreviousMonth={handleCopyFromPreviousMonth}
                />
              ))}
            </div>
          </>
        )}

        <p className="text-[11px] text-muted-foreground">
          M / E / N = shift first letter · O = Off · Tap a work day to edit · Select employees, then use top Save; card Save saves one employee
        </p>
      </div>

      <Dialog open={shiftPicker !== null} onOpenChange={(open) => !open && setShiftPicker(null)}>
        <DialogContent className="sm:max-w-md">
          {shiftPicker ? (
            <>
              <DialogHeader>
                <DialogTitle>Assign shift</DialogTitle>
              </DialogHeader>
              <p className="text-sm font-medium text-foreground">{shiftPicker.employeeName}</p>
              <p className="text-sm text-muted-foreground">
                {format(new Date(shiftPicker.day.date + "T12:00:00"), "EEEE, d MMMM yyyy")}
              </p>
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                <button
                  type="button"
                  className={cn(
                    "w-full text-left rounded-xl border px-3 py-3 text-sm hover:bg-muted transition-colors",
                    !shiftPicker.currentShiftId && "border-primary bg-primary/5"
                  )}
                  onClick={() => setCellShift(shiftPicker.employeeId, shiftPicker.day.date, null)}
                >
                  No shift / Off
                </button>
                {shiftOptions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={cn(
                      "w-full text-left rounded-xl border px-3 py-3 text-sm hover:bg-muted transition-colors",
                      shiftPicker.currentShiftId === s.id && "border-primary bg-primary/5"
                    )}
                    onClick={() => setCellShift(shiftPicker.employeeId, shiftPicker.day.date, s.id)}
                  >
                    <span className={cn("font-semibold mr-2", shiftLetterColorClass(s.id))}>
                      {shiftLegendLetter(s.name)}
                    </span>
                    <span className="text-foreground">{s.name}</span>
                    {s.startTime && s.endTime ? (
                      <span className="text-muted-foreground ml-1">
                        {s.startTime}–{s.endTime}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
